import React, { useEffect, useState } from 'react';
import { Amplify, Auth, Hub } from 'aws-amplify';
import AuthSettings from 'helpers/AuthSettings';
import { useSnackbar } from 'notistack';
import { useHistory, useLocation } from 'react-router-dom';


Amplify.configure(AuthSettings.CONFIG);

interface IAuthContext {
    isLoading: boolean
    isAuthenticated: boolean

    federatedSignIn: (returnUrl?: string) => Promise<void>
    signInWithCredentials: (clientId: string, clientSecret: string) => Promise<boolean>
    signInAndChangePassword: (clientId: string, clientSecret: string, newPassword: string) => Promise<boolean>
    forgotPassword: (email: string) => Promise<boolean>
    forgotPasswordSubmit: (clientId: string, code: string, password: string) => Promise<void>
    signOut: () => Promise<void>
    getToken: () => Promise<string | null>
}

export let authContext = React.createContext({} as IAuthContext);

let { Provider } = authContext;

let AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let history = useHistory();
    let location = useLocation();
    let { enqueueSnackbar } = useSnackbar();

    let [isLoading, setLoading] = useState(true);
    let [isAuthenticated, setAuthenticated] = useState(false);


    useEffect(() => {
        getAuthenticatedUser().then(() => setLoading(false));
    }, [])

    Hub.listen("auth", async ({ payload: { event, data } }) => {
        switch (event) {

            case "cognitoHostedUI":
                break;

            // Videndum Employees access
            case "customOAuthState":
                await getAuthenticatedUser();

                let decodedUrl = decodeURIComponent(data);
                if (decodedUrl) {
                    history.push(decodedUrl);
                } else
                    history.push('/');

                setLoading(false);
                break;

            // External user access
            case "signIn":
                await getAuthenticatedUser();

                let returnUrl = '/';

                if (location.search) {
                    let url = new URLSearchParams(location.search).get("returnUrl");
                    if (url)
                        returnUrl = url;
                }

                history.push(returnUrl);
                setLoading(false);
                break;

            case "tokenRefresh":
                await getAuthenticatedUser();
                setLoading(false);
                break;

            case "forgotPassword":
                history.push("/account/resetpassword", { email: data.username });
                break;

            case "forgotPasswordSubmit":
                history.replace('/account/login');
                break;

            case 'signIn_failure':
            case 'oAuthSignOut':
            case "signOut":
                history.push('/account/login')
                setAuthenticated(false);
                setLoading(false);
                break;

            case "signUp":
                break;

            default:
                setAuthenticated(false);
                setLoading(false);
                console.info(`[Dealers] Not managed event: ${event}`);
                break;
        }
    });



    let federatedSignIn = async (returnUrl?: string) => {
        try {
            //@ts-ignore
            await Auth.federatedSignIn({ provider: 'VidendumEntraId', customState: returnUrl });
        } catch (err: any) {
            enqueueSnackbar(err.message, { variant: 'error' });
        }
    }

    let signInWithCredentials = async (clientId: string, clientSecret: string) => {
        try {
            let cognitoUser = await Auth.signIn(clientId, clientSecret);

            if (cognitoUser.challengeName === "NEW_PASSWORD_REQUIRED") {
                history.replace('/account/changepassword', { clientId, clientSecret });
            }

            return true;

        } catch (error: any) {
            enqueueSnackbar(error.message, { variant: 'error' });
            return false;
        }
    }

    let signInAndChangePassword = async (clientId: string, clientSecret: string, newPassword: string) => {
        try {
            let cognitoUser = await Auth.signIn(clientId, clientSecret);
            await Auth.completeNewPassword(cognitoUser, newPassword);
            return true;

        } catch (error: any) {
            enqueueSnackbar(error.message, { variant: 'error' });
            return false;
        }
    }

    // To initiate the process of verifying the attribute like 'phone_number' or 'email'
    let forgotPassword = async (userId: string) => {
        try {
            await Auth.forgotPassword(userId);
            enqueueSnackbar('Verification code has been sent to your email', { variant: 'success' });
            return true;

        } catch (err: any) {
            enqueueSnackbar('Failed to sent verification code: ' + err.message, { variant: 'error' });
            return false;
        }
    }

    // To verify attribute with the code
    let forgotPasswordSubmit = async (clientId: string, code: string, password: string) => {
        try {
            await Auth.forgotPasswordSubmit(clientId, code, password);
            enqueueSnackbar('Your password has been changed', { variant: 'success' });

        } catch (err: any) {
            enqueueSnackbar('Failed to change the password: ' + err.message, { variant: 'error' });
        }
    }

    let signOut = async () => {
        try {
            await Auth.signOut();
        } catch (err: any) {
            enqueueSnackbar(err.message, { variant: 'error' });
        }
    }

    let getToken = async () => {
        let session = await Auth.currentSession();

        if (session?.isValid())
            return session.getIdToken().getJwtToken();
        else
            return null;
    }

    let getAuthenticatedUser = async () => {
        try {
            setLoading(true);
            await Auth.currentAuthenticatedUser();
            setAuthenticated(true);
            return true;

        } catch (e) {
            setAuthenticated(false);
            return false;
        }
    }


    return (
        <Provider
            value={{
                isLoading,
                isAuthenticated,
                federatedSignIn,
                signInWithCredentials,
                signInAndChangePassword,
                forgotPassword,
                forgotPasswordSubmit,
                signOut,
                getToken
            }}
        >
            {children}
        </Provider>
    )
}

export default AuthProvider;